import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { TileContainer } from '@vds/core';
import Image from '../../image/image/index.js';
import Text from '../../typography/text/index.js';
import '../../icons/component-icons/right-caret-regular/index.js';
import '../../icons/component-icons/left-caret-regular/index.js';
import ButtonGroup from '../../buttons/button-group/index.js';
import styles from './image-tile.module.scss.js';

const ImageTile = (_a) => {
    var _b;
    var { image, surface, title, subtitle, children, contentAlignment, buttonGroup, padding, background, elementSurface: elementSurfaceProp, height = 'auto', disableAnimation = false, contentAreaPadding, borderRadius = 'tile', onClick, href, width = '100%' } = _a, rest = __rest(_a, ["image", "surface", "title", "subtitle", "children", "contentAlignment", "buttonGroup", "padding", "background", "elementSurface", "height", "disableAnimation", "contentAreaPadding", "borderRadius", "onClick", "href", "width"]);
    /**
     * MUST destructure image prop to prevent console error of invalid props passed to DOM
     */
    const _c = image || {}, { hideBorderOnHover: imageHideBorderOnHover, position: imagePosition, width: imageWidth, aspectRatio: imageAspectRatio, fallbackColor: imageFallbackColor, borderRadius: imageBorderRadius, alt: imageAlt, inset: imageInset, src: imageSrc } = _c, moreImage = __rest(_c, ["hideBorderOnHover", "position", "width", "aspectRatio", "fallbackColor", "borderRadius", "alt", "inset", "src"]);
    const mappedSurface = String(surface).includes('dark') ? 'dark' : 'light';
    const elementSurface = elementSurfaceProp || background; // do background check here.
    const mappedBackgroundColor = String(elementSurface).includes('dark')
        ? 'dark'
        : 'light';
    const styledTile = [styles.styledTileContainer];
    const outerContentWrapper = [styles.outerWrapper];
    const imgBorder = [styles.imgBorder];
    const imageOuterWrapper = [styles.imageOuterWrapper];
    const imageWrapper = [styles.imageWrapper, styles[mappedSurface]];
    const contentWrapper = [styles.contentWrapper];
    const buttonGroupWrapper = [styles.buttonGroupWrapper];
    if (disableAnimation)
        styledTile.push(styles.disableScaling);
    if (imagePosition) {
        styledTile.push(styles[imagePosition]);
        outerContentWrapper.push(styles[imagePosition]);
        imgBorder.push(styles[imagePosition]);
        imageWrapper.push(styles[imagePosition]);
        contentWrapper.push(styles[imagePosition]);
    }
    if (['top', 'bottom'].includes(imagePosition !== null && imagePosition !== void 0 ? imagePosition : '')) {
        buttonGroupWrapper.push(styles.margin);
    }
    if (imageInset) {
        contentWrapper.push(styles.imageInset);
        imageWrapper.push(styles.imageInset);
        imgBorder.push(styles.imageInset);
        if (!(image === null || image === void 0 ? void 0 : image.hideBorderOnHover))
            styledTile.push(styles.imageInsetBorder);
    }
    if (['left', 'right'].includes(imagePosition !== null && imagePosition !== void 0 ? imagePosition : '') &&
        (buttonGroup === null || buttonGroup === void 0 ? void 0 : buttonGroup.position) === 'bottom') {
        buttonGroupWrapper.push(styles.margin);
    }
    if (['left', 'right'].includes(imagePosition !== null && imagePosition !== void 0 ? imagePosition : '') &&
        contentAlignment === 'middle') {
        contentWrapper.push(styles.center);
    }
    if (!onClick && !href) {
        styledTile.push(styles.noHover);
    }
    const getDefaultImgFallbackColor = () => {
        if (!image)
            return undefined;
        switch (mappedSurface) {
            case 'dark':
                return 'var(--vds-color-gray-900)';
            case 'light':
            default:
                return 'var(--vds-color-gray-200)';
        }
    };
    const getBorderRadius = () => {
        if (!borderRadius)
            return undefined;
        switch (borderRadius) {
            case 'hero':
                return 'var(--vds-tiles-hero-border-radius)';
            case 'tile':
                return 'var(--vds-tiles-standard-border-radius)';
            default:
                return borderRadius;
        }
    };
    const renderTitle = () => {
        var _a;
        const defaultTitleColor = elementSurface === 'limitedRed' || mappedBackgroundColor === 'dark'
            ? `var(--vds-color-white)`
            : `var(--vds-color-black)`;
        return (jsx("span", { className: styles.title, style: {
                '--title-bottom-spacing': title === null || title === void 0 ? void 0 : title.paddingBottom,
            }, children: jsx(Text, Object.assign({ bold: true, kind: "title", size: "small", primitive: "span" }, title, ((title === null || title === void 0 ? void 0 : title.tooltip) && {
                tooltip: Object.assign({ surface: elementSurface === 'limitedRed'
                        ? 'darkPrimary'
                        : elementSurface }, title.tooltip),
            }), { color: (title === null || title === void 0 ? void 0 : title.color) || defaultTitleColor, children: jsx("span", { className: styles.overflowWrapper, style: { WebkitLineClamp: (_a = title === null || title === void 0 ? void 0 : title.maxLines) !== null && _a !== void 0 ? _a : undefined }, children: title === null || title === void 0 ? void 0 : title.children }) })) }));
    };
    const renderSubtitle = () => {
        var _a;
        const defaultSubtitleColor = elementSurface === 'limitedRed' || mappedBackgroundColor === 'dark'
            ? `var(--vds-color-white)`
            : `var(--vds-color-black)`;
        return (jsx("span", { className: styles.subtitle, style: {
                '--subtitle-spacing': subtitle === null || subtitle === void 0 ? void 0 : subtitle.paddingBottom,
            }, children: jsx(Text, Object.assign({ kind: "body", size: "large", primitive: "span" }, subtitle, ((subtitle === null || subtitle === void 0 ? void 0 : subtitle.tooltip) && {
                tooltip: Object.assign({ surface: elementSurface === 'limitedRed'
                        ? 'darkPrimary'
                        : elementSurface }, subtitle.tooltip),
            }), { color: (subtitle === null || subtitle === void 0 ? void 0 : subtitle.color) || defaultSubtitleColor, children: jsx("span", { className: styles.overflowWrapper, style: { WebkitLineClamp: (_a = subtitle === null || subtitle === void 0 ? void 0 : subtitle.maxLines) !== null && _a !== void 0 ? _a : undefined }, children: subtitle === null || subtitle === void 0 ? void 0 : subtitle.children }) })) }));
    };
    return (jsx("div", { className: styledTile.join(' '), style: { height, width }, children: jsx(TileContainer, Object.assign({ height: "100%", padding: imageInset ? padding || 'var(--vds-space-3x)' : '0px', surface: surface, onClick: onClick, href: href, background: background, elementSurface: elementSurface, borderRadius: borderRadius }, rest, { width: "100%" // Let tileContainerWrapper determine the width
            , imageFallbackColor: undefined, children: jsxs("div", { className: outerContentWrapper.join(' '), children: [image && imageSrc && (jsxs(Fragment, { children: [jsx("div", { className: imageOuterWrapper.join(' '), style: {
                                    width: ['left', 'right'].includes(imagePosition !== null && imagePosition !== void 0 ? imagePosition : '')
                                        ? imageWidth || '50%'
                                        : '100%',
                                }, children: jsx(Image, Object.assign({ alt: imageAlt, src: imageSrc }, moreImage, { className: imageWrapper.join(' '), style: {
                                        '--img-aspect-ratio': imageAspectRatio,
                                        backgroundColor: imageFallbackColor || getDefaultImgFallbackColor(),
                                        width: '100%',
                                        '--img-border-radius': imageInset
                                            ? imageBorderRadius
                                            : getBorderRadius(),
                                    } })) }), !imageHideBorderOnHover && !imageInset && (jsx("div", { className: imgBorder.join(' ') }))] })), jsxs("div", { className: contentWrapper.join(' '), style: {
                            '--header-spacing': title === null || title === void 0 ? void 0 : title.paddingBottom,
                            '--tile-padding-no-img-inset': ['top', 'bottom'].includes(imagePosition !== null && imagePosition !== void 0 ? imagePosition : '') && !imageInset
                                ? padding
                                : undefined,
                            padding: contentAreaPadding, // Allow to add extra padding when inset is false and not top/bottom
                        }, children: [jsxs("div", { className: styles.contentHeader, children: [title && renderTitle(), subtitle && renderSubtitle()] }), children, buttonGroup && ((_b = buttonGroup === null || buttonGroup === void 0 ? void 0 : buttonGroup.items) === null || _b === void 0 ? void 0 : _b.length) !== 0 && (jsx("div", { className: buttonGroupWrapper.join(' '), style: {
                                    '--button-padding': buttonGroup.paddingTop,
                                }, children: jsx(ButtonGroup, Object.assign({ surface: elementSurface === 'limitedRed'
                                        ? 'darkPrimary'
                                        : elementSurface, alignment: "left", rowQuantity: { mobile: 2, tablet: 2, desktop: 2 } }, buttonGroup, { width: "100%" })) }))] })] }) })) }));
};

export { ImageTile as default };
